/**
 * Training data fetching and processing for PDF generation.
 * Extracted from FarmDetailsModal.tsx.
 */

import {
  TrainingEnrollmentDto,
  TrainingModuleDto,
  TrainingTopicDto,
  TrainingContentDto,
  ContentProgressItemDto,
} from "../../../../../api/types/dtos/training";
import { createTrainingEnrollmentService } from "../../../../../api/services/training-enrollment.service";
import { createTrainingProgressService } from "../../../../../api/services/training-progress.service";
import { createTrainingModuleService } from "../../../../../api/services/training-module.service";
import { createTrainingTopicService } from "../../../../../api/services/training-topic.service";
import { createTrainingContentService } from "../../../../../api/services/training-content.service";

/**
 * Training section with topics and progress.
 */
export interface TrainingSection {
  module: TrainingModuleDto;
  topics: TrainingTopicWithContent[];
  progressPercentage: number;
}

/**
 * Topic with completion status and contents.
 */
export interface TrainingTopicWithContent {
  topic: TrainingTopicDto;
  isCompleted: boolean;
  contents: TrainingContentDto[];
}

/**
 * Fetch training progress data for PDF generation.
 */
export async function fetchTrainingProgressData(
  ownOrgId: string | undefined,
  organizationId: string,
): Promise<TrainingSection[]> {
  if (!ownOrgId || !organizationId) {
    return [];
  }

  try {
    const enrollmentService = createTrainingEnrollmentService(organizationId);
    const enrollmentsResponse = await enrollmentService.findAll();
    const enrollments = enrollmentsResponse.data || [];

    if (enrollments.length === 0) {
      return [];
    }

    const trainingMap = new Map<string, TrainingEnrollmentDto[]>();
    enrollments.forEach((enrollment: TrainingEnrollmentDto) => {
      const existing = trainingMap.get(enrollment.trainingId) || [];
      existing.push(enrollment);
      trainingMap.set(enrollment.trainingId, existing);
    });

    const trainingPromises = Array.from(trainingMap.entries()).map(
      async ([trainingId, trainingEnrollments]) => {
        const enrollment = trainingEnrollments[0];

        const moduleService = createTrainingModuleService(ownOrgId, trainingId);
        const modulesResponse = await moduleService.findAll({ isActive: true });
        const modules = modulesResponse.data || [];

        const topicsPromises = modules.map(
          async (module: TrainingModuleDto) => {
            const topicService = createTrainingTopicService(
              ownOrgId,
              trainingId,
              module.id,
            );
            return topicService.findAll({ isActive: true });
          },
        );
        const topicsResponses = await Promise.all(topicsPromises);
        const topics = topicsResponses.flatMap(
          (response: any) => response.data || [],
        );

        const contentsPromises = topics.map(async (topic: TrainingTopicDto) => {
          const module = modules.find(
            (m: TrainingModuleDto) => m.id === topic.moduleId,
          );
          if (!module) return { data: [] };
          const contentService = createTrainingContentService(
            ownOrgId,
            trainingId,
            module.id,
            topic.id,
          );
          return contentService.findAll({ isActive: true });
        });
        const contentsResponses = await Promise.all(contentsPromises);
        const contents = contentsResponses.flatMap(
          (response: any) => response.data || [],
        );

        const progressService = createTrainingProgressService(organizationId, {
          enrollmentId: enrollment.id,
        });
        const progressResponse = await progressService.findAll();
        const progressData = progressResponse.data || [];

        const contentProgressArray: ContentProgressItemDto[] = progressData.map(
          (p: any) => ({
            progressId: p.id,
            contentId: p.contentId,
            isCompleted: p.isCompleted,
          }),
        );

        const isTopicCompleted = (
          topic: TrainingTopicDto,
          contents: TrainingContentDto[],
          contentProgress: ContentProgressItemDto[],
        ): boolean => {
          const topicContents = contents.filter((c) => c.topicId === topic.id);
          if (topicContents.length === 0) return false;
          const progressMap = new Map(
            contentProgress.map((cp) => [cp.contentId, cp.isCompleted]),
          );
          return topicContents.every((content) => {
            const progress = progressMap.get(content.id);
            return progress === true;
          });
        };

        const calculateSectionProgress = (
          module: TrainingModuleDto,
          topics: TrainingTopicDto[],
          contents: TrainingContentDto[],
          contentProgress: ContentProgressItemDto[],
        ): number => {
          const moduleTopics = topics.filter((t) => t.moduleId === module.id);
          let totalContribution = 0;
          moduleTopics.forEach((topic) => {
            if (isTopicCompleted(topic, contents, contentProgress)) {
              totalContribution += topic.progressContribution;
            }
          });
          return Math.min(Math.round(totalContribution), 100);
        };

        const sortedModules = [...modules].sort(
          (a: TrainingModuleDto, b: TrainingModuleDto) =>
            a.sequence - b.sequence,
        );

        const sections = sortedModules.map((module: TrainingModuleDto) => {
          const moduleTopics = topics
            .filter((t: TrainingTopicDto) => t.moduleId === module.id)
            .sort(
              (a: TrainingTopicDto, b: TrainingTopicDto) =>
                a.sequence - b.sequence,
            )
            .map((topic: TrainingTopicDto) => {
              const topicContents = contents.filter(
                (c: TrainingContentDto) => c.topicId === topic.id,
              );
              const isCompleted = isTopicCompleted(
                topic,
                contents,
                contentProgressArray,
              );
              return {
                topic,
                isCompleted,
                contents: topicContents,
              };
            });

          const progressPercentage = calculateSectionProgress(
            module,
            topics,
            contents,
            contentProgressArray,
          );

          return {
            module,
            topics: moduleTopics,
            progressPercentage,
          };
        });

        return sections;
      },
    );

    return (await Promise.all(trainingPromises)).flat();
  } catch (error) {
    console.error("Error fetching training data for PDF:", error);
    return [];
  }
}
